import { useRouterState } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useState, useEffect, useMemo } from 'react';

export interface PageTitleData {
  title: string;
  isLoading?: boolean;
  badge?: {
    rooms?: number;
    category?: string;
    price?: number;
  };
}

interface CachedPropertyQueryData {
  property?: {
    id?: string;
    street?: string;
    no?: string;
    zip?: string;
    city?: string;
    numberOfRooms?: number;
    category?: string;
    price?: number;
  };
  dossier?: {
    id?: string;
    address?: {
      street?: string;
      streetNumber?: string;
      zip?: string;
      location?: string;
    };
  };
  widgetProps?: {
    address?: NonNullable<CachedPropertyQueryData['dossier']>['address'];
    propertyForm?: {
      address?: NonNullable<CachedPropertyQueryData['dossier']>['address'];
    };
  };
  params?: {
    propertyId?: string;
  };
}

export function usePageTitle(): PageTitleData {
  const queryClient = useQueryClient();

  // Get the current pathname from the router state.
  // This is a narrow selector that only re-renders when the pathname changes.
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });

  // Subscribe to React Query cache changes so we can re-derive the title
  // when child route loader data arrives (e.g. DossierHomeLoaderData with
  // dossier.address). Without this, the useMemo below would only re-run on
  // pathname changes, not on query resolution.
  const [
    cacheVersion,
    setCacheVersion,
  ] = useState(0);
  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      // Observer option changes also fire during render; only data updates matter.
      if (event.type === 'updated' && event.action.type === 'success') {
        setCacheVersion((v) => v + 1);
      }
    });
    return unsubscribe;
  }, [queryClient]);

  // Derive the page title by scanning the React Query cache for address data.
  // This runs whenever cacheVersion or pathname changes, ensuring the header
  // updates as soon as child route queries resolve — even after client-side
  // navigation where the router state doesn't change.
  return useMemo(() => {
    // Reference cacheVersion so useMemo re-runs when queries resolve
    void cacheVersion;

    const cockpitMatch = pathname.match(/\/cockpit\/([^/]+)/);
    const dossierMatch = pathname.match(/\/dossier\/([^/]+)/);
    const propertyId = cockpitMatch?.[1];
    const dossierId = dossierMatch?.[1];

    const allQueries = queryClient
      .getQueryCache()
      .getAll()
      .sort((a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt);

    for (const query of allQueries) {
      const data = query.state.data as CachedPropertyQueryData | undefined;

      if (!data) continue;

      if (propertyId && data.property?.street) {
        const queryKey = query.queryKey;
        const matchesCurrentProperty =
          (Array.isArray(queryKey) &&
            queryKey.some(
              (part: unknown) =>
                typeof part === 'object' &&
                part !== null &&
                'propertyId' in part &&
                (part as { propertyId?: string }).propertyId === propertyId,
            )) ||
          data.property.id === propertyId ||
          data.params?.propertyId === propertyId;

        if (matchesCurrentProperty) {
          const { street, no, zip, city, numberOfRooms, category, price } =
            data.property;
          if (street && no && zip && city) {
            return {
              title: `${street} ${no}, ${zip} ${city}`,
              isLoading: false,
              badge: {
                rooms: numberOfRooms,
                category,
                price,
              },
            };
          }
        }
      }

      const dossierAddress =
        data.dossier?.address ||
        data.widgetProps?.propertyForm?.address ||
        data.widgetProps?.address;
      if (dossierId && dossierAddress?.street) {
        // CRITICAL FIX: Only match cache entries that belong to the CURRENT
        // dossier. The query key for dossier routes contains the dossierId
        // in its params (e.g. { dossierId: '426142352629' }). We check both
        // the query key params and the data's own dossier.id to ensure we
        // don't pick up stale address data from a different dossier that
        // happens to be in the cache from a previous navigation.
        const queryKey = query.queryKey;
        const matchesCurrentDossier =
          // Check if the query key params contain the matching dossierId
          (Array.isArray(queryKey) &&
            queryKey.some(
              (part: unknown) =>
                typeof part === 'object' &&
                part !== null &&
                'dossierId' in part &&
                (part as { dossierId?: string }).dossierId === dossierId,
            )) ||
          // Also check the data's own dossier.id as a fallback
          data.dossier?.id === dossierId;

        if (matchesCurrentDossier) {
          const { street, streetNumber, zip, location } = dossierAddress;
          if (street && streetNumber && zip && location) {
            return {
              title: `${street} ${streetNumber}, ${zip} ${location}`,
              isLoading: false,
            };
          }
        }
      }
    }

    if (propertyId || dossierId) {
      return { title: '', isLoading: true };
    }

    return { title: '', isLoading: false };
  }, [
    cacheVersion,
    pathname,
    queryClient,
  ]);
}
